import { Breadcrumb, Col, Row, Button, MenuProps, Dropdown, Select } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useParams, useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import ResetButton from "../UI/ResetButton/resetbutton";
import { Search } from "../UI/Search/Search";
import CancelBtn from "../UI/CancelButton/CancelBtn";
import CountListDetails from "./CountListDetails";
import FileDownloader from "./FileDownloader";
import FileUploader from "./FileUploader";
import { getCountListDetailsByID } from "../../api/inventoryCount";
import { CountDetailsInterface } from "./Interfaces";
import { getCategory } from "../../api/category";

interface CategoryInterface {
  label: string;
  value: number
}

const CountDetails = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const title = searchParams.get('title');
  const canEdit = searchParams.get('canEdit');


  const { id } = useParams();
  const { t } = useTranslation("common");
  const [tableKey, setTableKey] = useState(0);
  const [searchText, setSearchText] = useState("");

  const [countsDetails, setCountsDetails] = useState<CountDetailsInterface[]>([])
  const [countsDetailsSearch, setCountsDetailsSearch] = useState<CountDetailsInterface[]>([])
  const [categoryOptions, setCategoryOptions] = useState<CategoryInterface[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)

  const { mutateAsync: getCountsMutation, isLoading } = useMutation(
    "getCounts",
    ({ id }: { id: number }) =>
      getCountListDetailsByID({ id }),
    {
      onSuccess: ({ data }) => {
        setCountsDetails(data.data);
        setCountsDetailsSearch(data.data)
      },
    }
  );

  const { mutateAsync: getCategoryMutation, isLoading: isCategoryLoading } = useMutation(
    "getCategory",
    () =>
      getCategory(),
    {
      onSuccess: ({ data }) => {
        setCategoryOptions(data.map((item: any) => {
          return {
            label: item.name,
            value: item.id
          }
        }))
      },
    }
  );

  useEffect(() => {
    getCategoryMutation()
  }, [])

  useEffect(() => {
    getCountsMutation({ id: parseInt(id ?? '0') })
  }, [])

  const handleResetFilters = () => {
    if (!searchText && !selectedCategoryId) return
    setSearchText("");
    setTableKey((tableKey) => tableKey + 1);
    setCountsDetails(countsDetailsSearch)
    setSelectedCategoryId(null)
  };

  const handleSearch = () => {
    const searchTextLower = searchText.trim().toLowerCase();

    const filteredData = countsDetailsSearch.filter(item =>
      (selectedCategoryId === null || item.item.category.id === selectedCategoryId) &&
      (item.item.name.toLowerCase().includes(searchTextLower) ||
        item.item.barcode.toLowerCase().includes(searchTextLower))
    );
    setCountsDetails(filteredData);
  };

  useEffect(() => {
    handleSearch()
  }, [searchText])

  const baseURL = (window as any).REACT_APP_BASE_API_URL

  const items: MenuProps["items"] = [
    {
      label: (
        <FileDownloader
          fileName={'Sheet Price Lists Details'}
          btnName={t('download')}
          link={baseURL + `/counts/${id}/export`} />
      ),
      key: "1",
    },
    {
      label: (
        <FileUploader
          setCountsDetails={setCountsDetails}
          setCountsDetailsSearch={setCountsDetailsSearch}
          countsDetails={countsDetails}
          countsDetailsSearch={countsDetailsSearch}
          btnName={t('upload')}
          uploadUrl={baseURL + `/counts/${id}/import?limit=999999`} />
      ),
      key: "2",
    },
  ];
  const menuProps = {
    items,
  };

  const handleCategoryChange = (categoryId: number | null) => {
    setSelectedCategoryId(categoryId);

    const searchTextLower = searchText.trim().toLowerCase();

    const filteredData = countsDetailsSearch.filter(item =>
      (categoryId === null || item.item.category.id === categoryId) &&
      (item.item.name.toLowerCase().includes(searchTextLower) ||
        item.item.barcode.toLowerCase().includes(searchTextLower))
    );

    setCountsDetails(filteredData);
  };


  return (
    <>
      <Row gutter={[10, 10]}>
        <Col offset={1} span={22} style={{
          marginBottom: "10px",
        }}>
          <Breadcrumb
            items={[
              {
                title: <Link to="/main">{t("home")}</Link>,
              },
              {
                title: <span>{t("inventory")}</span>,
              },
              {
                title: <Link to="/inventory/count">{t("count")}</Link>,
              },
              {
                title: (
                  <span className="breadcrumbcolor">
                    {t("details")}
                  </span>
                ),
              },
            ]}
          />
        </Col>
        <Col className="editTitle" offset={1} span={22}>
          {title}
        </Col>

        <Col offset={1} span={4}>
          <Search
            value={searchText}
            onChange={(e: any) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={2}>
          <Select
            style={{ width: "110%" }}
            size="large"
            placeholder={t("category")}
            onChange={handleCategoryChange}
            value={selectedCategoryId !== null ? selectedCategoryId : undefined}
          >
            {categoryOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
            <Select.Option key={null} value={null}>
              {t("")}
            </Select.Option>
          </Select>
        </Col>
        <Col
          style={{ display: "flex", justifyContent: "flex-end", gap: "20px" }}
          offset={14}
          span={2}
        >
          <ResetButton
            onClick={() => {
              handleResetFilters();
            }}
          />
          <Dropdown trigger={["click"]} menu={menuProps} disabled={canEdit === 'false'}>
            <Button
              type="primary"
              style={{ background: "#1677FF", color: "#fff", height: "40px", backgroundColor: canEdit === 'false' ? 'gray' : "#1677FF" }}
            >
              <CaretDownOutlined />
              {t("action")}
            </Button>
          </Dropdown>
        </Col>
        <Col offset={1} span={22}>
          <CountListDetails
            tableKey={tableKey}
            countsDetailsSearch={countsDetailsSearch}
            countsDetails={countsDetails}
            id={id}
            setCountsDetails={setCountsDetails}
            setCountsDetailsSearch={setCountsDetailsSearch}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      <Row>
        <Col offset={1} style={{ paddingTop: "1rem" }}>
          <CancelBtn />
        </Col>
      </Row>
    </>
  );
};

export default CountDetails;
